<template>
  <ion-page>
    <ion-content :scroll-events="true" @ion-scroll="handleScroll($event)">
      <div class="main-container">
        <div class="h-full flex flex-col pb-320">
          <div class="px-16 pt-32 text-start">
            <h1 class="text-xl font-medium">Slutför anmälan</h1>
          </div>
          <div class="px-16 pt-24 border-b border-gray">
            <div class="text-md font-medium text-start">Datum</div>
            <div>
              <DateInput
                id="start"
                :label="'Händelsen började'"
                :default-date="incident && incident.date_start"
                :required="datesMandatory"
                @on-input="setStartDate"
              />
              <DateInput
                id="end"
                :label="'Händelsen slutade'"
                :default-date="incident && incident.date_end"
                :required="datesMandatory"
                @on-input="setEndDate"
              />
            </div>
            <div v-if="userValidated && invalidDates" class="headline-small mb-24 text-error text-start">
              Du måste ange datum och tid för när händelsen började och slutade.
            </div>
          </div>
          <div class="px-16 py-32 border-b border-gray">
            <CompanySelector
              :company="{ name: company.name, org_nr: company.org_nr }"
              :workplace="workplace"
              @set-workplace="selectWorkplace"
              @set-company="selectCompany"
              @set-sector="selectSector"
            />
            <div class="mt-24">
              <ion-toggle
                class="w-full"
                aria-label="Tertiary toggle"
                color="tertiary"
                justify="space-between"
                :checked="incidentOccurredOnWorkplace"
                @ion-change="(e) => incidentOccurredOnWorkplace = e.detail.checked"
              >
                <div class="headline-small">Händelsen inträffade på mitt arbetsställe</div>
              </ion-toggle>
              <div v-if="!incidentOccurredOnWorkplace" class="pt-24">
                <div class="headline-medium text-start">Var inträffade händelsen?</div>
                <form>
                  <TextInput
                    class="mt-24"
                    placeholder="Adress"
                    :value="incident.location_address"
                    @on-input="(input) => changeLocation('location_address', input)"
                  />
                  <TextInput
                    class="mt-16"
                    placeholder="Postnummer"
                    :value="incident.location_zip"
                    @on-input="(input) => changeLocation('location_zip', input)"
                  />
                  <TextInput
                    class="mt-16"
                    placeholder="Postort"
                    :value="incident.location_city"
                    @on-input="(input) => changeLocation('location_city', input)"
                  />
                </form>
              </div>
            </div>
            <div v-if="userValidated && (invalidCompany || invalidWorkplace)" class="headline-small mt-12 text-error text-start">
              Du måste ange vilket företag och arbetsplats du arbetar på.
            </div>
          </div>
          <div class="px-16 py-32">
            <div class="headline-medium text-start">Dina uppgifter</div>
            <div>
              <form>
                <TextInput
                  class="mt-24"
                  placeholder="Förnamn"
                  :value="user.first_name"
                  :invalid="userValidated && !user.first_name"
                  :error-text="'Fältet får inte vara tomt'"
                  @on-input="(input) => changeUser('first_name', input)"
                />
                <TextInput
                  class="mt-16"
                  placeholder="Efternamn"
                  :value="user.last_name"
                  :invalid="userValidated && !user.last_name"
                  :error-text="'Fältet får inte vara tomt'"
                  @on-input="(input) => changeUser('last_name', input)"
                />
                <TextInput
                  class="mt-16"
                  placeholder="E-postadress"
                  :value="user.email"
                  :invalid="userValidated && (!user.email || !emailValid(user.email))"
                  :error-text="!emailValid(user.email) ? 'Felaktig e-postadress' : 'Fältet får inte vara tomt'"
                  @on-input="(input) => changeUser('email', input)"
                />
                <TextInput
                  class="mt-16"
                  placeholder="Telefonnummer"
                  sub-text="Obligatoriskt"
                  :value="user.phone"
                  :invalid="userValidated && !user.phone"
                  :error-text="'Fältet får inte vara tomt'"
                  @on-input="(input) => changeUser('phone', input)"
                />
              </form>
            </div>
          </div>
          <div class="mt-auto px-16 py-24 flex justify-end flex-col">
            <Button
              type="primary"
              :text="'Förhandsgranska anmälan'"
              :icon="arrowForward"
              @click="incidentStore.onNext"
            />
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { IonPage, IonContent, IonToggle } from '@ionic/vue';
import { useIncidentStore } from '@/store/incident';
import { storeToRefs } from 'pinia';
import { Workplace } from '@/constants/types/api';
import DateInput from '@/components/form-elements/DateInput.vue';
import { computed, onMounted, ref, watch } from 'vue';
import TextInput from '@/components/form-elements/TextInput.vue';
import moment from 'moment/moment';
import CompanySelector from '@/components/CompanySelector.vue';
import { useUserStore } from '@/store/user';
import { useMandatoryIncidentDates } from '@/composeables/useMandatoryIncidentDates';
import Button from '@/components/Button.vue';
import { arrowForward } from 'ionicons/icons';

const emits = defineEmits<{
  (e: 'setScrolled', payload: boolean): void;
}>();

const startDate = ref('');
const endDate = ref('');
const incidentOccurredOnWorkplace = ref(true);
const selectedSector = ref<number | null>(null);

const incidentStore = useIncidentStore();
const userStore = useUserStore();
const { datesMandatory } = useMandatoryIncidentDates();

const company = ref({
  name: '',
  org_nr: '',
  sector: null as number | null,
  access_code: '',
});
const workplace = ref<Workplace | null>(null);

onMounted(() => {
  if (userStore.currentUser) {
    company.value.name = userStore.currentUser.company.name;
    company.value.org_nr = userStore.currentUser.company.org_nr;
    company.value.sector = userStore.currentUser.company.sector;
    company.value.access_code = userStore.currentUser.company.access_code;

    workplace.value = {
      Namn: userStore.currentUser.workplace.name,
      Gatuadress: userStore.currentUser.workplace.address,
      Postnummer: userStore.currentUser.workplace.zip,
      Postort: userStore.currentUser.workplace.city,
      ArbetsstalleID: userStore.currentUser.workplace.id,
    };
  }
});

const { incident, user, userValidated } = storeToRefs(incidentStore);

const setStartDate = (date: string | null) => {
  startDate.value = date || '';
  incidentStore.setField('date_start', date);
};

const setEndDate = (date: string | null) => {
  endDate.value = date || '';
  incidentStore.setField('date_end', date);
};

const selectWorkplace = (workplaceInput: Workplace) => {
  workplace.value = workplaceInput;
  incidentStore.setLocation(workplaceInput);
};

const selectCompany = ({ name, orgNr }) => {
  company.value.name = name;
  company.value.org_nr = orgNr;
  incidentStore.setCompany({ name, orgNr });
};

const selectSector = (value: number | string) => {
  selectedSector.value = value as number;
  incidentStore.setSector(value as number);
};

const changeUser = (key: 'email' | 'first_name' | 'last_name' | 'phone', input: string) => {
  incidentStore.setUserField(key, input);
};

const changeLocation = (key: 'location_address' | 'location_zip' | 'location_city', input: string) => {
  incidentStore.setOtherLocation({ key, input });
};

const invalidDates = computed(() => {
  return datesMandatory.value && (!incident.value.date_start || !incident.value.date_end);
});

const invalidCompany = computed(() => {
  return !incident.value.user.company.org_nr;
});

const invalidWorkplace = computed(() => {
  return !workplace.value;
});

const emailValid = (email: string | null) => {
  if (!email) {
    return false;
  } else {
    return email.match(
      /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    );
  }
};

// Prefilling with "now" is the non-mandatory behaviour. When the dates are
// mandatory the user has to pick them, so nothing is filled in for them.
watch(
  incident,
  () => {
    if (incident.value && !datesMandatory.value) {
      setStartDate(moment().format('YYYY-MM-DDTHH:mm:ss'));
      setEndDate(moment().format('YYYY-MM-DDTHH:mm:ss'));
    }
  }
);

const handleScroll = (e: CustomEvent) => {
  emits('setScrolled', e.detail.scrollTop > 50);
};

</script>
<style>
.native-wrapper {
  @apply border-t border-b border-l rounded-l-md border-gray;
}
</style>
